import { useState, useRef, useEffect, useCallback, memo } from "react";
import { CalendarEvent } from "@/Calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PaginatedEventsList, UnifiedEvent } from "./PaginatedEventsList";
import { EventDetails } from "./EventDetails";
import { format } from "date-fns";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

interface DailyEventsListProps {
  events: CalendarEvent[];
  selectedEvent: UnifiedEvent | null;
  onEventClick: (event: UnifiedEvent) => void;
  selectedDate?: number;
  isLoading?: boolean;
  viewDate?: Date;
}

const DailyEventsListComponent = ({ 
  events, 
  selectedEvent, 
  onEventClick,
  selectedDate,
  isLoading,
  viewDate
}: DailyEventsListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (selectedDate && scrollRef.current) {
      // Clear any existing scroll timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        // Use UTC to match how Calendar component creates dates
        const date = format(new Date(Date.UTC(currentYear, currentMonth, selectedDate)), 'yyyy-MM-dd');
        
        const dateElement = scrollRef.current?.querySelector(`[data-date="${date}"]`);
        if (dateElement) {
          dateElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        }
      }, 100);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [selectedDate]);

  const handleEventClick = useCallback((event: UnifiedEvent) => {
    onEventClick(event);
    setIsDetailsVisible(true);
  }, [onEventClick]);

  const handleCloseDetails = useCallback(() => {
    setIsDetailsVisible(false);
    // Create an empty event object with required fields to clear selection
    const emptyEvent: UnifiedEvent = {
      id: '',
      date: '',
      country: '',
      impact: 'Low',
      title: '',
      score: 0,
      currency: 'USD'
    };
    onEventClick(emptyEvent);
  }, [onEventClick]);

  return (
    <div className="relative h-full overflow-hidden flex-1 flex flex-col">
      <ScrollArea className="h-full flex-1">
        <div ref={scrollRef} className="max-w-full">
          <PaginatedEventsList
            events={events}
            selectedEvent={selectedEvent}
            onEventClick={handleEventClick}
            isLoading={isLoading}
            viewDate={viewDate}
          />
        </div>
      </ScrollArea>
      
      <Dialog open={isDetailsVisible} onOpenChange={handleCloseDetails}>
        <DialogContent className={cn(
          "p-0 gap-0 bg-background/95 backdrop-blur-sm",
          "border border-border/80 shadow-xl shadow-black/20",
          isMobile ? (
            "w-[95vw] max-w-[95vw] h-[80vh] max-h-[80vh] rounded-xl fixed top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]"
          ) : (
            "max-w-lg max-h-[80vh]"
          )
        )}>
          <button
            onClick={handleCloseDetails}
            className="absolute right-4 top-4 z-50 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          <ScrollArea className="w-full h-full">
            <EventDetails 
              event={selectedEvent} 
              onClose={handleCloseDetails} 
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Implement proper comparison function for memo
const arePropsEqual = (prevProps: DailyEventsListProps, nextProps: DailyEventsListProps) => {
  return (
    prevProps.events === nextProps.events &&
    prevProps.selectedEvent?.id === nextProps.selectedEvent?.id &&
    prevProps.selectedDate === nextProps.selectedDate &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.viewDate?.getTime() === nextProps.viewDate?.getTime()
  );
};

export const DailyEventsList = memo(DailyEventsListComponent, arePropsEqual);
